/**
 * Built-in note templates. Each returns a fresh TipTap JSON document so a new
 * note can start from a useful skeleton instead of a blank page. Kept as plain
 * data (no editor dependency) so the picker can preview them cheaply.
 */
import type { JSONContent } from "@tiptap/core";

export interface NoteTemplate {
  id: string;
  label: string;
  /** One-line description shown in the picker. */
  hint: string;
  /** Emoji used as the picker tile icon. */
  emoji: string;
  /** Default title applied to the created note. */
  title: string;
  build: () => JSONContent;
}

const h = (level: number, text: string): JSONContent => ({
  type: "heading",
  attrs: { level },
  content: [{ type: "text", text }],
});
const p = (text = ""): JSONContent =>
  text
    ? { type: "paragraph", content: [{ type: "text", text }] }
    : { type: "paragraph" };
const task = (text: string, checked = false): JSONContent => ({
  type: "taskItem",
  attrs: { checked },
  content: [{ type: "paragraph", content: [{ type: "text", text }] }],
});
const bullet = (text: string): JSONContent => ({
  type: "listItem",
  content: [{ type: "paragraph", content: [{ type: "text", text }] }],
});

export const NOTE_TEMPLATES: NoteTemplate[] = [
  {
    id: "blank",
    label: "Blank",
    hint: "Start from an empty page",
    emoji: "📄",
    title: "",
    build: () => ({ type: "doc", content: [p()] }),
  },
  {
    id: "todo",
    label: "To-do list",
    hint: "A checklist you can tick off",
    emoji: "✅",
    title: "To-do",
    build: () => ({
      type: "doc",
      content: [
        h(2, "To-do"),
        {
          type: "taskList",
          content: [task("First thing"), task("Second thing"), task("Third thing")],
        },
      ],
    }),
  },
  {
    id: "meeting",
    label: "Meeting notes",
    hint: "Agenda, notes, action items",
    emoji: "📝",
    title: "Meeting notes",
    build: () => ({
      type: "doc",
      content: [
        h(2, "Meeting notes"),
        p("Date · Attendees"),
        h(3, "Agenda"),
        { type: "bulletList", content: [bullet("Topic one"), bullet("Topic two")] },
        h(3, "Notes"),
        p(),
        h(3, "Action items"),
        { type: "taskList", content: [task("Owner — task")] },
      ],
    }),
  },
  {
    id: "journal",
    label: "Daily journal",
    hint: "Gratitude, focus, reflection",
    emoji: "🌤️",
    title: "Journal",
    build: () => ({
      type: "doc",
      content: [
        h(2, "Today"),
        h(3, "Grateful for"),
        { type: "bulletList", content: [bullet("…")] },
        h(3, "Today's focus"),
        { type: "taskList", content: [task("…")] },
        h(3, "Reflection"),
        p(),
      ],
    }),
  },
  {
    id: "project",
    label: "Project plan",
    hint: "Goal, milestones, tasks",
    emoji: "🎯",
    title: "Project plan",
    build: () => ({
      type: "doc",
      content: [
        h(2, "Project plan"),
        h(3, "Goal"),
        p(),
        h(3, "Milestones"),
        { type: "bulletList", content: [bullet("Milestone 1"), bullet("Milestone 2")] },
        h(3, "Tasks"),
        { type: "taskList", content: [task("…")] },
      ],
    }),
  },
];
